// ── STEP Product error codes ─────────────────────────────────────────────────
// Contract (backend release 2026-08-03): every FE-visible `error.code` — and the
// `code` on a streamed chat `error` chunk — is UPPER_SNAKE_CASE across all four
// STEP APIs. The extraction path used to emit a lower_snake mix
// (`awaiting_opco` next to `AI_OUTPUT_UNPARSEABLE` from adjacent steps of the
// *same* request); it has been normalised up to match the config APIs.
//
// `normaliseCode` upper-cases whatever arrives, so a stale workflow that still
// emits the old casing keeps matching these constants during a rollout.

export const STEP_ERROR = {
  // Request validation — extraction (renamed 2026-08-03, were lower_snake)
  AWAITING_OPCO: 'AWAITING_OPCO',
  AWAITING_USER: 'AWAITING_USER',
  NO_FILE_RECEIVED: 'NO_FILE_RECEIVED',
  MULTIPLE_FILES: 'MULTIPLE_FILES',
  INVALID_BASE64: 'INVALID_BASE64',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  UNSUPPORTED_MIME: 'UNSUPPORTED_MIME',
  // Document parsing — extraction (renamed 2026-08-03)
  PDF_PASSWORD_PROTECTED: 'PDF_PASSWORD_PROTECTED',
  PDF_CORRUPT: 'PDF_CORRUPT',
  PDF_EMPTY: 'PDF_EMPTY',
  SPREADSHEET_PARSE_FAILED: 'SPREADSHEET_PARSE_FAILED',
  XLSX_TOO_MANY_SHEETS: 'XLSX_TOO_MANY_SHEETS',
  NO_PRIMARY_DATA_TAB: 'NO_PRIMARY_DATA_TAB',
  LLM_REQUEST_FAILED: 'LLM_REQUEST_FAILED',
  // Extraction — already UPPER_SNAKE, unchanged
  AI_OUTPUT_UNPARSEABLE: 'AI_OUTPUT_UNPARSEABLE',
  EXCEL_PAYLOAD_TOO_LARGE: 'EXCEL_PAYLOAD_TOO_LARGE',
  EXTRACTION_FAILED: 'EXTRACTION_FAILED',
  // Config APIs + upload proxy — unchanged
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  VERSION_CONFLICT: 'VERSION_CONFLICT',
  NOT_FOUND: 'NOT_FOUND',
  PROMPT_NOT_CONFIGURED: 'PROMPT_NOT_CONFIGURED',
  STEP_AUTH_FAILED: 'STEP_AUTH_FAILED',
  STEP_UPLOAD_FAILED: 'STEP_UPLOAD_FAILED',
  STEP_REJECTED: 'STEP_REJECTED',
} as const;

export type StepErrorCode = (typeof STEP_ERROR)[keyof typeof STEP_ERROR];

// Tolerant matcher: upper-cases so pre-2026-08-03 lower_snake codes still map.
export const normaliseCode = (code: unknown): string =>
  typeof code === 'string' ? code.trim().toUpperCase() : '';

// Extra guidance appended under the server's own message. Only for codes where
// the user can actually do something — the workflow's wording stays primary.
const HINTS: Record<string, string> = {
  [STEP_ERROR.AWAITING_OPCO]: 'Pick an OpCo above, then send the document again.',
  [STEP_ERROR.AWAITING_USER]: 'Your sign-in did not reach the workflow — refresh the page and retry.',
  [STEP_ERROR.NO_FILE_RECEIVED]: 'Attach the supplier document and send again.',
  [STEP_ERROR.MULTIPLE_FILES]: 'Send one document per message.',
  [STEP_ERROR.FILE_TOO_LARGE]: 'The limit is 25 MB — split the workbook or remove embedded images.',
  [STEP_ERROR.UNSUPPORTED_MIME]: 'Supported formats are PDF, XLSX, XLS and CSV.',
  [STEP_ERROR.PDF_PASSWORD_PROTECTED]: 'Remove the password from the PDF and upload it again.',
  [STEP_ERROR.PDF_CORRUPT]: 'The PDF could not be rendered — re-export it and retry.',
  [STEP_ERROR.PDF_EMPTY]: 'The PDF has no pages to read.',
  [STEP_ERROR.SPREADSHEET_PARSE_FAILED]: 'The workbook could not be read — re-save it as .xlsx and retry.',
  [STEP_ERROR.XLSX_TOO_MANY_SHEETS]: 'Keep the workbook to 10 sheets or fewer.',
  [STEP_ERROR.NO_PRIMARY_DATA_TAB]: 'No product data tab was found — check the sheet has a header row of product columns.',
  [STEP_ERROR.EXCEL_PAYLOAD_TOO_LARGE]: 'Split the file into smaller batches and extract each one.',
  [STEP_ERROR.INVALID_BASE64]: 'The upload was corrupted in transit — retry.',
  [STEP_ERROR.LLM_REQUEST_FAILED]: 'The model call failed — retry in a moment.',
  [STEP_ERROR.AI_OUTPUT_UNPARSEABLE]: 'The model returned an unreadable result — retry.',
  [STEP_ERROR.STEP_AUTH_FAILED]: 'STEP rejected the credentials — contact the STEP administrator.',
  [STEP_ERROR.STEP_UPLOAD_FAILED]: 'STEP could not be reached — retry in a moment.',
};

// Server message + a remediation line when we recognise the code.
export const describeStepError = (code: unknown, message: string): string => {
  const hint = HINTS[normaliseCode(code)];
  const text = message.trim();
  if (!hint) return text || 'The workflow reported an error.';
  return text.includes(hint) ? text : `${text || 'The workflow reported an error.'}\n\n*${hint}*`;
};
